import { create } from "zustand";
import type { User, Hero, Balance, Screen, LevelUpInfo, PlayerItem, HeroClass, HeroGender } from "../types";
import { ApiError, setToken, loadToken, clearToken } from "../api/client";
import { authApi } from "../api/auth";
import { heroApi } from "../api/hero";
import { economyApi } from "../api/economy";
import { dailyRewardApi } from "../api/dailyReward";
import { configApi } from "../api/config";
import { getTelegramInitData, IS_DEBUG } from "../utils/telegram";
import { buildLevelUpInfo } from "../utils/levelUp";
import type { ClientConfig, DailyRewardStatus } from "../types";
import { useCampaignRunStore } from "./useCampaignRunStore";

export const INFO_CHAT_CHANNEL = "info";

const ADMIN_ASSET_PATHS_KEY = "mk_admin_asset_paths";
const ADMIN_CAMPAIGN_BALANCE_KEY = "mk_admin_campaign_balance";

function loadAdminShowAssetPaths(): boolean {
  try {
    return localStorage.getItem(ADMIN_ASSET_PATHS_KEY) === "1";
  } catch {
    return false;
  }
}

function loadAdminCampaignBalanceMode(): boolean {
  try {
    return localStorage.getItem(ADMIN_CAMPAIGN_BALANCE_KEY) === "1";
  } catch {
    return false;
  }
}

interface GameState {
  // Auth
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;

  // Navigation
  activeScreen: Screen;

  // Game data
  hero: Hero | null;
  needsCharacterCreation: boolean;
  balance: Balance | null;
  dailyRewardStatus: DailyRewardStatus | null;
  clientConfig: ClientConfig | null;
  /** Snapshot taken before an XP-granting action (battle, dungeon node, etc.). */
  heroSnapshot: Hero | null;
  /** Shown after the battle-result overlay is dismissed. */
  pendingLevelUp: LevelUpInfo | null;
  /** Loot items queued to show one-by-one after battles/dungeons. */
  pendingLootQueue: import("../types").PlayerItem[];

  /** Skill code → display name from skill tree API. */
  skillNames: Record<string, string>;
  /** Skill code → mana cost at the hero's current level (for battle UI preview). */
  skillManaCosts: Record<string, number>;
  /** Skill code → cooldown turns at the hero's current level (battle skill bar). */
  skillCooldowns: Record<string, number>;

  /** Ephemeral system lines for the info chat channel (not persisted server-side). */
  infoChatMessages: import("../types").ChatMessage[];

  /** Admin debug: show asset file paths under icons (localStorage). */
  adminShowAssetPaths: boolean;
  /** Admin: post-campaign-fight balance tuning modal (localStorage). */
  adminCampaignBalanceMode: boolean;

  // Loading & errors
  isLoading: boolean;
  error: string | null;

  // Actions
  init: () => Promise<void>;
  logout: () => void;
  setScreen: (screen: Screen) => void;
  refreshHero: () => Promise<void>;
  createHero: (heroClass: HeroClass, gender: HeroGender, name: string) => Promise<void>;
  refreshBalance: () => Promise<void>;
  refreshDailyReward: () => Promise<void>;
  refreshClientConfig: () => Promise<void>;
  claimDailyReward: () => Promise<import("../types").DailyRewardClaimResult>;
  captureHeroSnapshot: () => void;
  checkLevelUp: () => void;
  refreshHeroAndCheckLevelUp: () => Promise<void>;
  dismissLevelUp: () => void;
  enqueueLoot: (items: PlayerItem[]) => void;
  popLoot: () => void;
  collectIdle: () => Promise<number>;
  refreshSkillNames: () => Promise<void>;
  setSkillNames: (names: Record<string, string>) => void;
  pushInfoMessages: (texts: string[]) => void;
  clearError: () => void;
  setAdminShowAssetPaths: (enabled: boolean) => void;
  setAdminCampaignBalanceMode: (enabled: boolean) => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  activeScreen: "main",
  hero: null,
  needsCharacterCreation: false,
  balance: null,
  dailyRewardStatus: null,
  clientConfig: null,
  heroSnapshot: null,
  pendingLevelUp: null,
  pendingLootQueue: [],
  skillNames: {},
  skillManaCosts: {},
  skillCooldowns: {},
  infoChatMessages: [],
  adminShowAssetPaths: loadAdminShowAssetPaths(),
  adminCampaignBalanceMode: loadAdminCampaignBalanceMode(),
  isLoading: false,
  error: null,

  init: async () => {
    set({ isLoading: true, error: null });
    void get().refreshClientConfig();
    try {
      // Try existing token first
      const existingToken = loadToken();
      if (existingToken) {
        try {
          const user = await authApi.getMe();
          set({ token: existingToken, user, isAuthenticated: true });
          await Promise.all([get().refreshHero(), get().refreshBalance(), get().refreshDailyReward()]);
          await useCampaignRunStore.getState().syncActiveRun();
          return;
        } catch {
          clearToken();
        }
      }

      // Auth via Telegram
      const initData = getTelegramInitData();

      if (!initData) {
        if (IS_DEBUG) {
          // Dev mode: test account via /auth/dev
          try {
            const { access_token, user } = await authApi.devLogin();
            setToken(access_token);
            set({ token: access_token, user, isAuthenticated: true });
            await Promise.all([get().refreshHero(), get().refreshBalance(), get().refreshDailyReward()]);
            await useCampaignRunStore.getState().syncActiveRun();
            return;
          } catch {
            set({ error: "Dev auth failed. Make sure the backend is running." });
            return;
          }
        }
        set({ error: "Open the app via Telegram." });
        return;
      }

      const { access_token, user } = await authApi.loginWithTelegram(initData);
      setToken(access_token);
      set({ token: access_token, user, isAuthenticated: true });
      await Promise.all([get().refreshHero(), get().refreshBalance(), get().refreshDailyReward()]);
      await useCampaignRunStore.getState().syncActiveRun();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Authentication failed" });
    } finally {
      set({ isLoading: false });
    }
  },

  logout: () => {
    clearToken();
    set({
      token: null,
      user: null,
      isAuthenticated: false,
      hero: null,
      balance: null,
    });
  },

  setScreen: (screen) => {
    const { user } = get();
    if (
      (screen === "admin" ||
        screen === "adminCharacter" ||
        screen === "adminGrants" ||
        screen === "debug") &&
      !user?.is_admin
    ) {
      set({ activeScreen: "main" });
      return;
    }

    const campaign = useCampaignRunStore.getState();
    if (
      campaign.isRunLocked &&
      campaign.run?.status === "ACTIVE" &&
      (screen === "battle" || screen === "campaign")
    ) {
      set({ activeScreen: "campaignRun" });
      void campaign.syncActiveRun();
      return;
    }

    set({ activeScreen: screen });
  },

  refreshHero: async () => {
    try {
      const hero = await heroApi.getHero();
      set({ hero, needsCharacterCreation: false, error: null });
      void get().refreshSkillNames();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        set({ hero: null, needsCharacterCreation: true, error: null });
        return;
      }
      set({ error: e instanceof Error ? e.message : "Failed to load hero" });
    }
  },

  createHero: async (heroClass, gender, name) => {
    const hero = await heroApi.createHero(heroClass, gender, name);
    set({ hero, needsCharacterCreation: false, error: null });
  },

  captureHeroSnapshot: () => {
    const { hero } = get();
    if (hero) set({ heroSnapshot: { ...hero, stats: { ...hero.stats } } });
  },

  checkLevelUp: () => {
    const { hero, heroSnapshot } = get();
    if (!hero || !heroSnapshot) return;
    const info = buildLevelUpInfo(heroSnapshot, hero);
    set({ heroSnapshot: null, pendingLevelUp: info ?? null });
  },

  refreshHeroAndCheckLevelUp: async () => {
    await get().refreshHero();
    get().checkLevelUp();
  },

  dismissLevelUp: () => set({ pendingLevelUp: null }),

  enqueueLoot: (items) => {
    if (!items.length) return;
    set((s) => ({ pendingLootQueue: [...s.pendingLootQueue, ...items] }));
  },

  popLoot: () =>
    set((s) => ({
      pendingLootQueue: s.pendingLootQueue.slice(1),
    })),

  refreshBalance: async () => {
    try {
      const balance = await economyApi.getBalance();
      set({ balance });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load balance" });
    }
  },

  refreshDailyReward: async () => {
    try {
      const dailyRewardStatus = await dailyRewardApi.getStatus();
      set({ dailyRewardStatus, error: null });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load daily reward" });
    }
  },

  refreshClientConfig: async () => {
    try {
      const clientConfig = await configApi.getClient();
      set({ clientConfig });
    } catch {
      // Non-fatal — battle haptics fall back to no vibration
    }
  },

  claimDailyReward: async () => {
    const result = await dailyRewardApi.claim();
    set({ dailyRewardStatus: result.status });
    await Promise.all([get().refreshBalance(), get().refreshHero()]);
    return result;
  },

  collectIdle: async () => {
    try {
      const res = await economyApi.collectIdle();
      await get().refreshBalance();
      return res.collected_gold;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to collect" });
      return 0;
    }
  },

  refreshSkillNames: async () => {
    try {
      const tree = await heroApi.getSkillTree();
      const names: Record<string, string> = { ...(tree.skill_names ?? {}) };
      const costs: Record<string, number> = {};
      const cooldowns: Record<string, number> = {};
      const levels = get().hero?.skill_levels ?? {};
      for (const entry of tree.skills) {
        names[entry.skill_name] = entry.display_name;
        const lvl = Math.max(1, levels[entry.skill_name] ?? 1);
        const row = entry.levels.find((l) => l.level === lvl) ?? entry.levels[0];
        if (row) {
          costs[entry.skill_name] = row.mana_cost;
          cooldowns[entry.skill_name] = row.cooldown_sec ?? 0;
        }
      }
      set({ skillNames: names, skillManaCosts: costs, skillCooldowns: cooldowns });
    } catch {
      // Non-fatal — UI falls back to generic labels
    }
  },

  setSkillNames: (names) => set({ skillNames: names }),

  pushInfoMessages: (texts) => {
    if (!texts.length) return;
    const stamp = Date.now();
    const rows = texts.map((text, index) => ({
      id: -(stamp + index),
      channel_code: INFO_CHAT_CHANNEL,
      channel_name: "Info",
      user_id: 0,
      display_name: "System",
      text,
      created_at: new Date(stamp + index).toISOString(),
    }));
    set((state) => ({
      infoChatMessages: [...state.infoChatMessages, ...rows].slice(-200),
    }));
  },

  clearError: () => set({ error: null }),

  setAdminShowAssetPaths: (enabled) => {
    try {
      localStorage.setItem(ADMIN_ASSET_PATHS_KEY, enabled ? "1" : "0");
    } catch {
      // ignore quota / private mode
    }
    set({ adminShowAssetPaths: enabled });
  },

  setAdminCampaignBalanceMode: (enabled) => {
    try {
      localStorage.setItem(ADMIN_CAMPAIGN_BALANCE_KEY, enabled ? "1" : "0");
    } catch {
      // ignore
    }
    set({ adminCampaignBalanceMode: enabled });
  },
}));
